import React, { useState, useEffect } from 'react';
import { Form, Input, Select, Button, Space, Row, Col, DatePicker } from 'antd';
import { SearchOutlined, FilterOutlined } from '@ant-design/icons';
import { User } from '../../../services/user';

const { Option } = Select;
const { RangePicker } = DatePicker;

interface CaseFiltersProps {
  form: any;
  onSearch: () => void;
  onReset: () => void;
  loading: boolean;
  users?: User[];
  activeTab?: 'responsible' | 'assistant' | 'all';
}

// 审级枚举配置
const CASE_LEVEL_OPTIONS = {
  '民事': [
    '一审',
    '二审',
    '申请再审',
    '申请抗议',
    '再审审理',
    '执行',
    '仲裁',
    '劳动仲裁',
    '申诉',
  ],
  '刑事': [
    '侦查',
    '审查起诉',
    '一审',
    '二审',
    '申请再审',
    '再审审理',
    '死刑复核',
    '申诉',
    '刑事速裁',
  ],
  '行政诉讼': [
    '复议',
    '一审',
    '二审',
    '申请再审',
    '申请抗诉',
    '再审审理',
    '执行',
    '申诉',
  ],
};

const CaseFilters: React.FC<CaseFiltersProps> = ({
  form,
  onSearch,
  onReset,
  loading,
  users = [],
  activeTab = 'all',
}) => {
  const [caseType, setCaseType] = useState<string>('');

  // 监听案件类型变化
  useEffect(() => {
    const currentCaseType = form.getFieldValue('case_type');
    setCaseType(currentCaseType || '');
  }, [form]);

  // 案件类型改变时清空审级
  const handleCaseTypeChange = (value: string) => {
    setCaseType(value || '');
    form.setFieldsValue({ case_level: undefined });
  };

  // 获取当前案件类型对应的审级选项
  const caseLevelOptions = caseType && CASE_LEVEL_OPTIONS[caseType as keyof typeof CASE_LEVEL_OPTIONS]
    ? CASE_LEVEL_OPTIONS[caseType as keyof typeof CASE_LEVEL_OPTIONS]
    : [];
  return (
    <Form form={form} layout="vertical" style={{ marginBottom: 16 }}>
      <Row gutter={[16, 16]}>
        <Col xs={24} sm={12} md={8} lg={6}>
          <Form.Item name="keyword" label="关键词搜索">
            <Input
              placeholder="搜索案件名称、编号"
              prefix={<SearchOutlined />}
              allowClear
            />
          </Form.Item>
        </Col>
        <Col xs={24} sm={12} md={8} lg={6}>
          <Form.Item name="status" label="案件状态">
            <Select placeholder="请选择案件状态" allowClear>
              <Option value="active">进行中</Option>
              <Option value="closed">已结案</Option>
              <Option value="suspended">已暂停</Option>
            </Select>
          </Form.Item>
        </Col>
        <Col xs={24} sm={12} md={8} lg={6}>
          <Form.Item name="case_type" label="案件类型">
            <Select 
              placeholder="请选择案件类型" 
              allowClear
              onChange={handleCaseTypeChange}
            >
              <Option value="民事">民事</Option>
              <Option value="刑事">刑事</Option>
              <Option value="行政诉讼">行政诉讼</Option>
            </Select>
          </Form.Item>
        </Col>
        <Col xs={24} sm={12} md={8} lg={6}>
          <Form.Item name="case_level" label="审级">
            <Select 
              mode="multiple"
              placeholder={caseType ? "请选择审级（可多选）" : "请先选择案件类型"}
              allowClear
              disabled={!caseType}
              maxTagCount="responsive"
            >
              {caseLevelOptions.map((level) => (
                <Option key={level} value={level}>
                  {level}
                </Option>
              ))}
            </Select>
          </Form.Item>
        </Col>
      </Row>
      <Row gutter={[16, 16]}>
        <Col xs={24} sm={12} md={8}>
          <Form.Item name="accept_date_range" label="承接日期范围">
            <RangePicker 
              style={{ width: '100%' }}
              placeholder={['开始日期', '结束日期']}
            />
          </Form.Item>
        </Col>
        <Col xs={24} sm={12} md={8}>
          <Form.Item name="court_date_range" label="开庭时间范围">
            <RangePicker 
              style={{ width: '100%' }}
              showTime
              placeholder={['开始时间', '结束时间']}
            />
          </Form.Item>
        </Col>
        {/* 只在"全部案件"Tab显示负责律师搜索框 */}
        {activeTab === 'all' && (
          <Col xs={24} sm={12} md={8}>
            <Form.Item name="responsible_lawyer_id" label="负责律师">
              <Select 
                placeholder="请选择负责律师" 
                allowClear 
                showSearch
                optionFilterProp="children"
              >
                {users.map((user) => (
                  <Option key={user.id} value={user.id}>
                    {user.real_name || user.username}
                  </Option>
                ))}
              </Select>
            </Form.Item>
          </Col>
        )}
      </Row>
    </Form>
  );
};

export default CaseFilters;
